import {
  GLYPH_SIZE,
  arrowHeadPath,
  drawingPath,
  drawingShown,
  exportDrawings,
  exportRegionColor,
  fillable,
  fogPath,
  fogged,
  imageTypeOf,
  javaIsBlank,
  mapGrid,
  pinColor,
  pinIcon,
  pinShown,
  regionLabelPositions,
  regionShown,
  sortedPins,
  type ExportAtlas,
  type ExportMap,
  type MapDrawing,
  type ResolvedIcon,
} from '@realmery/realm-core';
import { FOG_COLOR, PIN_PATH, TOKEN_RADIUS, darker, parseHex } from './mapFigure.ts';

/**
 * A map flattened into a JPEG (docs/PARITY-STEP-4-PLAN.md section 11), as the desktop's
 * `MapExport.renderJpeg` draws it for the Markdown export: the image at most `maxWidth`
 * across, with its grid, regions, drawings, region names, and pins and tokens with their
 * icons and names painted in. A player-facing rendering has the fog painted solid and leaves
 * out what's under it. Empty bytes when the image can't be decoded, as on the desktop.
 */

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

const rgba = (hex: string, alpha: number) => {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r},${g},${b},${Math.round(Math.max(0, Math.min(1, alpha)) * 255) / 255})`;
};

async function decode(data: Uint8Array): Promise<ImageBitmap | null> {
  const type = imageTypeOf(data);
  if (!type) return null;
  try {
    return await createImageBitmap(new Blob([data as Uint8Array<ArrayBuffer>], { type }));
  } catch {
    return null;
  }
}

function canvasOf(width: number, height: number): { canvas: OffscreenCanvas | HTMLCanvasElement; g: Context } | null {
  const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(width, height) : Object.assign(document.createElement('canvas'), { width, height });
  const g = canvas.getContext('2d') as Context | null;
  return g ? { canvas, g } : null;
}

async function jpeg(canvas: OffscreenCanvas | HTMLCanvasElement): Promise<Uint8Array> {
  const blob =
    canvas instanceof OffscreenCanvas
      ? await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.88 })
      : await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
  return blob ? new Uint8Array(await blob.arrayBuffer()) : new Uint8Array();
}

/** Dark text with a light halo, centred on (x, y) (`haloText`). */
function haloText(g: Context, text: string, font: string, x: number, y: number): void {
  g.font = font;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.lineWidth = 3.5;
  g.strokeStyle = 'rgba(255,255,255,0.92)';
  g.strokeText(text, x, y);
  g.fillStyle = '#1F1A2B';
  g.fillText(text, x, y);
}

function drawDrawing(g: Context, drawing: MapDrawing, k: number): void {
  if (drawing.kind === 'TEXT') {
    const at = drawing.points[0] ?? { x: 0, y: 0 };
    const size = Math.max(1, Math.round(drawing.textSize * k));
    g.font = `bold ${size}px Georgia, serif`;
    g.textAlign = 'left';
    g.textBaseline = 'top';
    g.lineJoin = 'round';
    g.lineCap = 'round';
    g.lineWidth = Math.max(2, (drawing.textSize * k) / 7);
    g.strokeStyle = 'rgba(255,250,240,0.9)';
    g.strokeText(drawing.text === '' ? ' ' : drawing.text, at.x * k, at.y * k);
    g.fillStyle = rgba(drawing.strokeColor, 1);
    g.fillText(drawing.text === '' ? ' ' : drawing.text, at.x * k, at.y * k);
    return;
  }
  g.save();
  g.scale(k, k);
  const shape = new Path2D(drawingPath(drawing));
  if (fillable(drawing.kind) && drawing.fillColor !== null) {
    g.fillStyle = rgba(drawing.fillColor, drawing.fillOpacity);
    g.fill(shape);
  }
  g.lineWidth = drawing.strokeWidth;
  g.lineJoin = 'round';
  g.lineCap = drawing.dashed ? 'butt' : 'round';
  g.setLineDash(drawing.dashed ? [drawing.strokeWidth * 3, drawing.strokeWidth * 2] : []);
  g.strokeStyle = rgba(drawing.strokeColor, 1);
  g.stroke(shape);
  const head = arrowHeadPath(drawing);
  if (head !== '') {
    const arrow = new Path2D(head);
    g.fillStyle = rgba(drawing.strokeColor, 1);
    g.fill(arrow);
    g.setLineDash([]);
    g.lineWidth = Math.max(1, drawing.strokeWidth / 3);
    g.stroke(arrow);
  }
  g.restore();
}

/** A pin's icon `size` across at (cx, cy): a white glyph, a picture in a circle, or a dot (`drawIcon`; emoji are a dot here). */
function drawIcon(g: Context, icon: ResolvedIcon | null, picture: ImageBitmap | null, cx: number, cy: number, size: number): void {
  if (icon?.kind === 'GLYPH') {
    g.save();
    g.translate(cx - size / 2, cy - size / 2);
    g.scale(size / GLYPH_SIZE, size / GLYPH_SIZE);
    g.fillStyle = '#ffffff';
    g.fill(new Path2D(icon.path), icon.evenOdd ? 'evenodd' : 'nonzero');
    g.restore();
    return;
  }
  if (icon?.kind === 'PICTURE' && picture) {
    const side = Math.min(picture.width, picture.height);
    g.save();
    g.beginPath();
    g.ellipse(cx, cy, size / 2, size / 2, 0, 0, Math.PI * 2);
    g.clip();
    g.drawImage(picture, Math.trunc((picture.width - side) / 2), Math.trunc((picture.height - side) / 2), side, side, cx - size / 2, cy - size / 2, size, size);
    g.restore();
    return;
  }
  const dot = (4.5 * size) / 16;
  g.fillStyle = '#ffffff';
  g.beginPath();
  g.ellipse(cx, cy, dot, dot, 0, 0, Math.PI * 2);
  g.fill();
}

export async function renderMapJpeg(atlas: ExportAtlas, m: ExportMap, includeHidden: boolean, maxWidth: number): Promise<Uint8Array> {
  const source = await decode(m.map.imageData);
  if (!source) return new Uint8Array();
  const pictures = new Map<string, ImageBitmap | null>();
  try {
    const k = Math.min(1, maxWidth / source.width);
    const w = Math.max(1, Math.round(source.width * k));
    const h = Math.max(1, Math.round(source.height * k));
    const made = canvasOf(w, h);
    if (!made) return new Uint8Array();
    const { canvas, g } = made;
    g.imageSmoothingQuality = 'high';
    g.drawImage(source, 0, 0, w, h);

    // The grid, in the image's own pixels, one pixel wide.
    g.strokeStyle = rgba(m.map.grid.colorHex, m.map.grid.opacity);
    g.lineWidth = 1;
    g.beginPath();
    for (const [x1, y1, x2, y2] of mapGrid({ ...m, width: source.width, height: source.height })) {
      g.moveTo(Math.round(x1 * k) + 0.5, Math.round(y1 * k) + 0.5);
      g.lineTo(Math.round(x2 * k) + 0.5, Math.round(y2 * k) + 0.5);
    }
    g.stroke();

    const labelScale = Math.max(1, w / 1000);
    for (const region of m.regions) {
      if (!regionShown(m, region, includeHidden)) continue;
      const polygon = new Path2D();
      region.points.forEach((p, i) => (i === 0 ? polygon.moveTo(p.x * k, p.y * k) : polygon.lineTo(p.x * k, p.y * k)));
      polygon.closePath();
      const hex = exportRegionColor(atlas, region);
      g.fillStyle = rgba(hex, region.visible ? 0.22 : 0.12);
      g.fill(polygon);
      g.strokeStyle = rgba(hex, 1);
      g.lineWidth = 2.5;
      g.lineJoin = 'round';
      g.lineCap = 'butt';
      g.setLineDash(region.visible ? [] : [8, 5]);
      g.stroke(polygon);
      g.setLineDash([]);
    }
    for (const drawing of exportDrawings(m, includeHidden)) if (drawingShown(m, drawing, includeHidden)) drawDrawing(g, drawing, k);
    if (fogged(m, includeHidden)) {
      g.save();
      g.scale(k, k);
      g.fillStyle = FOG_COLOR;
      g.fill(new Path2D(fogPath(true, m.revealed, source.width, source.height)), 'evenodd');
      g.restore();
    }
    for (const [region, c] of regionLabelPositions(m, includeHidden, labelScale / k)) {
      haloText(g, region.label, `italic ${Math.round(17 * labelScale)}px Georgia, serif`, c.x * k, c.y * k);
    }

    for (const pin of sortedPins(m)) {
      if (!pinShown(m, pin, includeHidden)) continue;
      const x = pin.x * k;
      const y = pin.y * k;
      const icon = pinIcon(atlas, pin);
      let picture: ImageBitmap | null = null;
      if (icon?.kind === 'PICTURE') {
        if (!pictures.has(icon.id)) pictures.set(icon.id, await decode(icon.data));
        picture = pictures.get(icon.id) ?? null;
      }
      const color = pinColor(atlas, pin);
      const token = pin.style === 'TOKEN';
      g.globalAlpha = pin.visible ? 1 : 0.6;
      if (token) {
        const r = TOKEN_RADIUS * labelScale;
        g.fillStyle = rgba(color, 1);
        g.beginPath();
        g.ellipse(x, y, r, r, 0, 0, Math.PI * 2);
        g.fill();
        g.strokeStyle = '#ffffff';
        g.lineWidth = 1.5 * labelScale;
        g.stroke();
        const inner = (TOKEN_RADIUS - 3) * labelScale;
        if (icon?.kind !== 'PICTURE') {
          g.fillStyle = darker(color);
          g.beginPath();
          g.ellipse(x, y, inner, inner, 0, 0, Math.PI * 2);
          g.fill();
        }
        drawIcon(g, icon, picture, x, y, icon?.kind === 'PICTURE' ? inner * 2 : inner * 1.35);
      } else {
        g.save();
        g.translate(x, y);
        g.scale(labelScale, labelScale);
        const body = new Path2D(PIN_PATH);
        g.fillStyle = rgba(color, 1);
        g.fill(body);
        g.strokeStyle = '#ffffff';
        g.lineWidth = 1.5;
        g.stroke(body);
        g.restore();
        drawIcon(g, icon, picture, x, y - 22 * labelScale, (icon?.kind === 'PICTURE' ? 23 : 16) * labelScale);
      }
      g.globalAlpha = 1;
      if (!javaIsBlank(pin.label)) {
        haloText(g, pin.label, `bold ${Math.round(12 * labelScale)}px system-ui, sans-serif`, x, y + (token ? TOKEN_RADIUS + 10 : 16) * labelScale);
      }
    }
    return await jpeg(canvas);
  } finally {
    source.close();
    for (const picture of pictures.values()) picture?.close();
  }
}

/**
 * Just the map with its fog painted solid, at its own size (`renderFoggedJpeg`): the
 * picture a player-facing page links to, so nothing under the fog is in the files.
 */
export async function renderFoggedJpeg(m: ExportMap): Promise<Uint8Array> {
  const source = await decode(m.map.imageData);
  if (!source) return new Uint8Array();
  try {
    const made = canvasOf(source.width, source.height);
    if (!made) return new Uint8Array();
    const { canvas, g } = made;
    g.drawImage(source, 0, 0);
    g.fillStyle = FOG_COLOR;
    g.fill(new Path2D(fogPath(true, m.revealed, source.width, source.height)), 'evenodd');
    return await jpeg(canvas);
  } finally {
    source.close();
  }
}

/** The fogged pictures a site below Owner needs, by slug. */
export async function renderFoggedJpegs(atlas: ExportAtlas, includeHidden: boolean): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  for (const m of atlas.maps) if (fogged(m, includeHidden)) out.set(m.slug, await renderFoggedJpeg(m));
  return out;
}

/** Every map's flattened picture, by slug, for the Markdown export. */
export async function renderMapJpegs(atlas: ExportAtlas, includeHidden: boolean, maxWidth = 1600): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  for (const m of atlas.maps) out.set(m.slug, await renderMapJpeg(atlas, m, includeHidden, maxWidth));
  return out;
}
